import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Search, MapPin, Shield, Sparkles, AlertCircle } from "lucide-react";
import api from "@/lib/api";


export default function DocumentLibrary() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);

  useEffect(() => {
    api.get("/documents/library").then((r) => setData(r.data));
  }, []);

  // BLOCK 107 ,  deep-link support: /app/documents/library?state=XX auto-selects.
  useEffect(() => {
    const st = (searchParams.get("state") || "").toUpperCase();
    if (st) setSelected(st);
  }, [searchParams]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/documents/state-psa/${selected}`).then((r) => setSelectedDetail(r.data));
  }, [selected]);

  if (!data) return <div className="text-slate-400 py-10 text-center">Loading library...</div>;

  const states = data.states
    .filter((s) => statusFilter === "all" || s.template_status.toLowerCase() === statusFilter)
    .filter((s) => !filter || s.state_name.toLowerCase().includes(filter.toLowerCase()) || s.state_code.includes(filter.toUpperCase()));

  return (
    <div className="space-y-6" data-testid="document-library-page">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Legal Reference</div>
        <h1 className="font-heading text-4xl tracking-tight">State Requirements</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-3xl">
          A state-by-state legal reference for purchase-agreement requirements across all 50 US
          states plus DC ,  the state's deed type, closing-agent custom, transfer-tax notes, and
          mandatory disclosures to review <span className="text-slate-300">before you generate a document</span>.
          This is a reference to check what each jurisdiction requires ,  not a place to download or
          copy a template directly.
        </p>
      </header>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-4 gap-3">
        <SummaryCard label="States Covered" value={`${data.total_states}`} />
        <SummaryCard label="Full Templates" value={`${data.full_count}`} accent="emerald" />
        <SummaryCard label="Standard Templates" value={`${data.standard_count}`} accent="sky" />
        <SummaryCard label="Attorney-Closing States" value={`${data.attorney_states_count}`} accent="amber" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="library-filters">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <input
            data-testid="library-search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search states..."
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none"
          />
        </div>
        <div className="flex gap-1" data-testid="library-status-filter">
          {[
            { id: "all", label: "All" },
            { id: "full", label: "Full" },
            { id: "standard", label: "Standard" },
          ].map((s) => (
            <button key={s.id}
                    data-testid={`library-filter-${s.id}`}
                    onClick={() => setStatusFilter(s.id)}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                      statusFilter === s.id
                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                        : "bg-white/0 text-slate-400 border-white/10 hover:text-slate-200"
                    }`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Map (US grid) + selected detail */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 ae-card p-4" data-testid="library-state-map">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-emerald-400" />
            <div className="text-[10px] uppercase tracking-widest text-slate-500">All states</div>
            <div className="ml-auto flex items-center gap-3 text-[10px] text-slate-500">
              <LegendDot color="bg-emerald-500" label="Full" />
              <LegendDot color="bg-sky-500" label="Standard" />
              <LegendDot color="bg-amber-500" label="Attorney" />
            </div>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-9 gap-1.5">
            {states.map((s) => (
              <button key={s.state_code}
                      onClick={() => setSelected(s.state_code)}
                      data-testid={`library-state-${s.state_code}`}
                      title={`${s.state_name} - ${s.template_status}${s.attorney_required ? " - Attorney" : ""}`}
                      className={`relative aspect-square text-[11px] font-mono rounded-sm border transition-all hover:scale-105 ${
                        selected === s.state_code ? "ring-2 ring-white/40 " : ""
                      } ${
                        s.template_status === "Full"
                          ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-200"
                          : "bg-sky-500/15 border-sky-500/40 text-sky-200"
                      }`}>
                {s.state_code}
                {s.attorney_required && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-400 ring-1 ring-amber-700" />
                )}
              </button>
            ))}
          </div>
          {states.length === 0 && <div className="text-center text-sm text-slate-500 py-6">No states match.</div>}
        </div>

        {/* Selected state detail */}
        <div className="ae-card p-4" data-testid="library-state-detail">
          {!selected && (
            <div className="text-center text-sm text-slate-500 py-10">
              <FileText className="h-6 w-6 text-slate-700 mx-auto mb-2" />
              Select a state to view its template detail.
            </div>
          )}
          {selected && !selectedDetail && <div className="text-slate-500 text-sm py-6">Loading...</div>}
          {selectedDetail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-baseline justify-between">
                <h3 className="font-heading text-xl">{selectedDetail.state_name}</h3>
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                  selectedDetail.template_status === "Full"
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                    : "bg-sky-500/15 text-sky-300 border-sky-500/40"
                }`}>
                  {selectedDetail.template_status}
                </span>
              </div>
              <Detail label="Deed Type" value={selectedDetail.deed_type} />
              <Detail label="Closing By" value={selectedDetail.closing_by} />
              <Detail label="Transfer Tax" value={selectedDetail.transfer_tax_note} />
              {selectedDetail.attorney_required && (
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-2 mt-2"
                     data-testid="library-attorney-warning">
                  <div className="flex items-start gap-2 text-xs text-amber-100">
                    <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-300" />
                    <span>{selectedDetail.attorney_warning}</span>
                  </div>
                </div>
              )}
              {selectedDetail.disclosures && (
                <details className="text-xs">
                  <summary className="text-slate-400 cursor-pointer hover:text-slate-200">State-mandated disclosures</summary>
                  <div className="mt-2 text-slate-400 whitespace-pre-line leading-relaxed">
                    {selectedDetail.disclosures}
                  </div>
                </details>
              )}
              <div className="text-[10px] text-slate-500 pt-2 border-t border-white/8">
                {selectedDetail.clauses_count} state-specific clauses appended to the PSA.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full table */}
      <div className="ae-card p-4" data-testid="library-state-table">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-emerald-400" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500">All states (alphabetical)</div>
          <div className="ml-auto text-[10px] text-slate-500">Last verified {data.last_verified_at}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left py-2">State</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Deed Type</th>
                <th className="text-left py-2">Closing</th>
                <th className="text-left py-2">Attorney</th>
              </tr>
            </thead>
            <tbody>
              {states.map((s) => (
                <tr key={s.state_code} className="border-b border-white/5 hover:bg-white/3 cursor-pointer"
                    onClick={() => setSelected(s.state_code)}
                    data-testid={`library-row-${s.state_code}`}>
                  <td className="py-2 text-slate-200 font-mono">{s.state_code} <span className="text-slate-500 ml-2">{s.state_name}</span></td>
                  <td>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                      s.template_status === "Full"
                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                        : "bg-sky-500/15 text-sky-300 border-sky-500/40"
                    }`}>{s.template_status}</span>
                  </td>
                  <td className="text-slate-400">{s.deed_type}</td>
                  <td className="text-slate-400">{s.closing_by}</td>
                  <td>{s.attorney_required && <span className="text-amber-300 text-xs">Required</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex items-start gap-2 max-w-3xl">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          Standard templates are master clauses adapted to the state's deed type, transfer tax, and disclosure requirements.
          We recommend review by a local real estate attorney before execution.
        </span>
      </div>
    </div>
  );
}


function SummaryCard({ label, value, accent }) {
  const cls = accent === "emerald" ? "text-emerald-300"
    : accent === "sky" ? "text-sky-300"
    : accent === "amber" ? "text-amber-300"
    : "text-slate-100";
  return (
    <div className="ae-card p-4" data-testid={`library-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-heading text-3xl tracking-tight mt-1 ${cls}`}>{value}</div>
    </div>
  );
}


function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`h-2.5 w-2.5 rounded-sm ${color}/40`} />
      <span className={`h-2.5 w-2.5 rounded-sm ${color} -ml-2.5`} />
      <span className="ml-1">{label}</span>
    </span>
  );
}


function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      <div className="text-slate-200 text-xs leading-relaxed">{value || ", "}</div>
    </div>
  );
}
